/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-mixins, ember/require-computed-property-dependencies, no-unused-vars, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import StudentReportState from '@linc-technologies/linced-common/enums/reports/student-report-state';
import { isiOS } from '@linc-technologies/linced-common/utils/platform';
import ReportingBaseMixin from 'linced-app/mixins/reporting-base';
import { periodLabelAt } from 'linced-app/utils/reporting';

export default class StudentController extends Controller.extend(
  ReportingBaseMixin,
) {
  @service
  dates;

  @service
  permissions;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  studentReportActions;

  @service
  toaster;

  isiOS = isiOS();

  // Void and Send-back-to-draft are restricted to tenant admins and above.
  @computed('session.scopes.[]')
  get isTenantAdmin() {
    return this.permissions.meet(Scopes.TENANT_ADMIN);
  }

  person = null;
  studentReports = null;
  @tracked templateVersionMap = null;
  isStudentWriteRoute = false;
  isStudentReviewRoute = false;
  @tracked
  writeDialogOpen = false;
  @tracked
  isCreatingDraft = false;
  @tracked
  voidConfirmOpen = false;
  @tracked
  voidingEntry = null;
  @tracked
  isVoiding = false;
  @tracked
  voidRecreateConfirmOpen = false;
  @tracked
  voidRecreatingEntry = null;
  @tracked
  reopenConfirmOpen = false;
  @tracked
  reopeningEntry = null;
  @tracked
  isReopening = false;

  @computed('groupController.selectedGroup')
  get selectedGroup() {
    return this.groupController.get('selectedGroup');
  }

  // _reportItem normalises a StudentReport DS.Model into the shape the
  // student-report-card component consumes. Used by both activeReports and
  // historicReports so the per-card data is identical between the two
  // sections; only the grouping differs.
  _reportItem(report) {
    let templateId = report.belongsTo('reportTemplateId').id();
    let template = this.store.peekRecord('reports/report-template', templateId);
    let state = report.get('state');
    let stateEntry = StudentReportState.values.find((v) => v.value === state);
    let periodIndex = report.get('periodIndex') || 0;
    return {
      report,
      template,
      periodIndex,
      templateName: template ? template.get('name') : 'Report',
      // Names the period; the range alone is ambiguous for a year-wrapping
      // template, where the stored index follows the by-month sort rather than
      // the declared order.
      periodLabel: template ? periodLabelAt(template, periodIndex) : '',
      isTemplateInactive: this._isTemplateInactive(templateId),
      isTemplateDeleted: this.isTemplateDeleted(templateId),
      canVoidRecreate: this.canVoidRecreate(templateId),
      stateLabel: stateEntry ? stateEntry.label : '',
      stateClass: this._stateClass(state),
      canReopen: state === StudentReportState.READY_TO_REVIEW,
    };
  }

  // The period a template is currently in, from the group's counts response.
  // The backend resolves it against the school's rollover — the only place
  // that knows which calendar year a period's month/day falls in — so nothing
  // here recomputes it. Null when the counts have not arrived, which callers
  // must treat as "unknown" rather than as period 0.
  _currentPeriodIndexFor(templateId) {
    let entry = (this.groupController.templateDraftInfo || {})[templateId];
    return entry && typeof entry.periodIndex === 'number'
      ? entry.periodIndex
      : null;
  }

  // _isActiveReport returns true when the report is for the current academic
  // year and at its template's current period. Everything else is historic
  // (future-period entries, retired-template reports, and prior-period
  // reports regardless of completion state).
  //
  // Which templates qualify is the counts response's call too, not
  // activeReportTemplates', so this page and the group page always agree. The
  // active counts carry only templates open for writing (active and not
  // deleted), so a deactivated or deleted template has no entry, its period
  // reads null, and its reports file as historic.
  _isActiveReport(report) {
    let versionMap = this.templateVersionMap || {};
    let currentYear = this.dates.currentAcademicYear;

    if (report.get('year') !== currentYear) return false;
    let templateId = report.belongsTo('reportTemplateId').id();
    let currentId = versionMap[templateId] || templateId;
    let currentPeriodIndex = this._currentPeriodIndexFor(currentId);
    if (currentPeriodIndex === null) return false;
    return report.get('periodIndex') === currentPeriodIndex;
  }

  // activeReports — flat list, alphabetised by template name. Renders in
  // the Active section above Historic.
  @computed(
    'studentReports.@each.{state,year,periodIndex}',
    'templateVersionMap',
    'groupController.{parentController.activeReportTemplates.[],templateDraftInfo}',
    'dates.currentAcademicYear',
  )
  get activeReports() {
    let reports = this.studentReports;
    if (!reports) return [];
    return reports
      .filter((r) => this._isActiveReport(r))
      .map((r) => this._reportItem(r))
      .sort((a, b) =>
        (a.templateName || '').localeCompare(b.templateName || ''),
      );
  }

  // historicReports — everything not in activeReports, grouped by academic
  // year DESC, sorted within year by period end month ASC then template
  // name ASC. Dumped all (void, published, in-progress for retired
  // templates) per the rollover plan.
  @computed(
    'studentReports.@each.{state,year,periodIndex}',
    'templateVersionMap',
    'groupController.{parentController.activeReportTemplates.[],templateDraftInfo}',
    'dates.currentAcademicYear',
  )
  get historicReports() {
    let reports = this.studentReports;
    if (!reports) return [];

    let grouped = {};
    for (let report of reports) {
      if (this._isActiveReport(report)) continue;
      let year = report.get('year');
      if (!year) continue;
      (grouped[year] ??= []).push(report);
    }

    return Object.keys(grouped)
      .sort((a, b) => Number(b) - Number(a))
      .map((year) => ({
        year,
        reports: grouped[year]
          .map((r) => this._reportItem(r))
          .sort((a, b) => {
            let aEnd = this._periodEndMonth(a.template, a.periodIndex);
            let bEnd = this._periodEndMonth(b.template, b.periodIndex);
            return (
              aEnd - bEnd ||
              (a.templateName || '').localeCompare(b.templateName || '')
            );
          }),
      }));
  }

  // Build a set of active template IDs that already have a non-void student
  // report for the period the template is in now. Uses the templateVersionMap
  // (built by the route via /current) to resolve old template IDs to their
  // current version without chain walking.
  //
  // A report from an earlier period or year doesn't cover the template: the
  // student still needs one for the current period. When the counts haven't
  // arrived the current period is unknown, so any current-year report covers
  // it rather than offering a template that may already have a draft.
  @computed(
    'studentReports.@each.{state,year,periodIndex}',
    'templateVersionMap',
    'groupController.{parentController.activeReportTemplates.[],templateDraftInfo}',
    'dates.currentAcademicYear',
  )
  get _coveredTemplateIds() {
    let reports = this.studentReports;
    let versionMap = this.templateVersionMap || {};
    let activeTemplates =
      this.get('groupController.parentController.activeReportTemplates') || [];
    if (!reports || !activeTemplates.length) return new Set();

    let activeIds = new Set(activeTemplates.map((t) => t.get('id')));
    let coveredIds = new Set();
    let currentYear = this.dates.currentAcademicYear;

    reports.forEach((report) => {
      if (report.get('state') === StudentReportState.VOID) return;
      if (report.get('year') !== currentYear) return;

      let templateId = report.belongsTo('reportTemplateId').id();
      let currentId = versionMap[templateId] || templateId;
      if (!activeIds.has(currentId)) return;

      let currentPeriodIndex = this._currentPeriodIndexFor(currentId);
      if (
        currentPeriodIndex === null ||
        report.get('periodIndex') === currentPeriodIndex
      ) {
        coveredIds.add(currentId);
      }
    });

    return coveredIds;
  }

  @computed(
    '_coveredTemplateIds',
    'groupController.{parentController.activeReportTemplates.[],templateDraftInfo}',
  )
  get allTemplatesCovered() {
    let activeTemplates =
      this.get('groupController.parentController.activeReportTemplates') || [];
    return (
      activeTemplates.length > 0 &&
      this._coveredTemplateIds.size >= activeTemplates.length
    );
  }

  // Active templates that don't yet have a non-void student report
  @computed(
    '_coveredTemplateIds',
    'groupController.{parentController.activeReportTemplates.[],templateDraftInfo}',
  )
  get availableTemplates() {
    let activeTemplates =
      this.get('groupController.parentController.activeReportTemplates') || [];
    let covered = this._coveredTemplateIds;
    return activeTemplates.filter((t) => !covered.has(t.get('id')));
  }

  @action
  backToGroup() {
    this.router.transitionTo('reporting.group');
  }

  @action
  openWriteDialog() {
    this.writeDialogOpen = true;
  }

  @action
  closeWriteDialog() {
    // Block user-initiated close attempts (X, backdrop, ESC) while a draft
    // is mid-flight so they can't abandon createDraft halfway through.
    if (this.isCreatingDraft) return;
    this.writeDialogOpen = false;
  }

  @action
  async selectTemplateAndWrite(template) {
    this.isCreatingDraft = true;
    let unblockUnload = this.studentReportActions.blockUnload();
    try {
      let newReport;
      try {
        let person = this.person;
        // Silently resolve to the current version if the picked template
        // has been superseded since the page loaded.
        let resolved =
          await this.studentReportActions.resolveCurrentTemplate(template);
        // Stamp the period the backend resolved for this template, not a
        // locally recomputed one. Falls back to the first period only when
        // the counts have not loaded, which leaves nothing to stamp against.
        let periodIndex = this._currentPeriodIndexFor(resolved.get('id')) ?? 0;
        newReport = await this.studentReportActions.createDraft({
          student: person,
          template: resolved,
          periodIndex,
          year: this.dates.currentAcademicYear,
        });
      } catch (e) {
        this.toaster.error(
          'Failed to create report draft. Please refresh the page and try again.',
        );
        await this.studentReportActions
          .refreshStudentReports(this, this.person.get('id'))
          .catch(() => null);
        return;
      }

      try {
        this.writeDialogOpen = false;
        await this.studentReportActions.refreshStudentReports(
          this,
          this.person.get('id'),
        );
        this.router.transitionTo(
          'reporting.group.student.write',
          newReport.get('id'),
        );
      } catch (e) {
        this.toaster.error(
          'Report created but could not be opened — please reload.',
        );
      }
    } finally {
      unblockUnload();
      this.isCreatingDraft = false;
    }
  }

  @action
  viewReport(entry) {
    let state = entry.report.get('state');
    let reportId = entry.report.get('id');
    if (state === StudentReportState.DRAFT) {
      this.router.transitionTo('reporting.group.student.write', reportId);
    } else if (state === StudentReportState.READY_TO_REVIEW) {
      this.router.transitionTo('reporting.group.student.review', reportId);
    } else if (
      state === StudentReportState.READY_TO_PUBLISH ||
      state === StudentReportState.PUBLISHED
    ) {
      this.router.transitionTo('reporting.group.student.view', reportId);
    }
  }

  @action
  openVoidConfirm(entry) {
    this.voidingEntry = entry;
    this.voidConfirmOpen = true;
  }

  @action
  closeVoidConfirm() {
    if (this.isVoiding) return;
    this.voidConfirmOpen = false;
    this.voidingEntry = null;
  }

  @action
  async confirmVoid() {
    let entry = this.voidingEntry;
    if (!entry) return;
    // These three can act on a report in a closed period, and the group hub
    // no longer refetches historic counts on every return. Mark it so the
    // trip back re-queries — see refreshHistoricIfNeeded.
    this.groupController.markHistoricStale();
    this.isVoiding = true;
    try {
      await this.studentReportActions.void(entry.report);
      this.voidConfirmOpen = false;
      this.voidingEntry = null;
    } catch (e) {
      this.toaster.error('Failed to void report');
    } finally {
      this.isVoiding = false;
    }
  }

  @action
  openVoidRecreate(entry) {
    // The card hides the button; this guards a caller reaching it another way.
    if (!entry || !entry.canVoidRecreate) return;
    this.voidRecreatingEntry = entry;
    this.voidRecreateConfirmOpen = true;
  }

  @action
  closeVoidRecreate() {
    if (this.isVoiding) return;
    this.voidRecreateConfirmOpen = false;
    this.voidRecreatingEntry = null;
  }

  // Void the published report and recreate it as a fresh draft, then land the
  // user on the new draft's write view. Mirrors the View Report screen's
  // void-and-edit flow (controllers/reporting/group/student/view.js) so it's
  // reachable directly from the report history timeline. The recreate endpoint
  // requires the report to be voided first and copies content against the
  // CURRENT (latest) template version.
  //
  // recreate returns 409 when a report for the same school/student/period/year
  // already exists on the latest template. Because the void must happen first,
  // a 409 leaves the report voided with no new copy — so we surface that
  // clearly. We're already on the list here, so we just refresh in place.
  @action
  async confirmVoidRecreate() {
    let entry = this.voidRecreatingEntry;
    if (!entry) return;
    // See confirmVoid.
    this.groupController.markHistoricStale();
    this.isVoiding = true;
    let voided = false;
    try {
      let oldReport = entry.report;

      await this.studentReportActions.void(oldReport);
      voided = true;
      let draft = await this.studentReportActions.recreate(oldReport);

      await this.studentReportActions.refreshStudentReports(
        this,
        this.person.get('id'),
      );

      this.router.transitionTo(
        'reporting.group.student.write',
        draft.get('id'),
      );
    } catch (e) {
      let isConflict =
        voided &&
        e &&
        (e.status === 409 || (e.message && e.message.includes('409')));
      this.toaster.error(
        isConflict
          ? 'This report was voided, but a report for this student already exists on the latest template for this period — a new copy was not created.'
          : voided
            ? 'The report was voided, but creating the new copy failed. Please try again.'
            : 'Failed to void the report. Please try again.',
      );
      await this.studentReportActions
        .refreshStudentReports(this, this.person.get('id'))
        .catch(() => null);
    } finally {
      this.isVoiding = false;
      this.voidRecreateConfirmOpen = false;
      this.voidRecreatingEntry = null;
    }
  }

  @action
  openReopenConfirm(entry) {
    this.reopeningEntry = entry;
    this.reopenConfirmOpen = true;
  }

  @action
  closeReopenConfirm() {
    if (this.isReopening) return;
    this.reopenConfirmOpen = false;
    this.reopeningEntry = null;
  }

  @action
  async confirmReopen() {
    let entry = this.reopeningEntry;
    if (!entry) return;
    // See confirmVoid.
    this.groupController.markHistoricStale();
    this.isReopening = true;
    try {
      await this.studentReportActions.reopen(entry.report);
      this.reopenConfirmOpen = false;
      this.reopeningEntry = null;
    } catch (e) {
      this.toaster.error('Failed to send report back to draft');
    } finally {
      this.isReopening = false;
    }
  }

  // Judged on the template's current version, not the one the report was
  // written against. A report on v1 of a template now on v3 is normal, and
  // flagging every superseded version would put the chip on most rows.
  //
  // Tested for truthiness, not against false: isActive is a proto3 bool sent
  // with omitempty, so an inactive template arrives with no isActive key and
  // the attribute reads undefined. customise/templates splits its active and
  // inactive lists the same way.
  //
  // Deactivation only. Deletion is independent server-side and has its own
  // messaging. False when the current record isn't in the store: an absent
  // record is not evidence of anything, and a chip is a claim.
  _isTemplateInactive(templateId) {
    let versionMap = this.templateVersionMap || {};
    let currentId = versionMap[templateId] || templateId;
    let current = this.store.peekRecord('reports/report-template', currentId);
    if (!current) return false;
    return !current.isActive;
  }

  // Judged on the current version for the same reason: deletion stamps only
  // the terminal, so the version a report was written against reads as live.
  // A deleted template takes no new reports, which includes the fresh copy
  // Void and edit makes. False when the current record isn't in the store,
  // as above. Public because the nested view route asks it too.
  isTemplateDeleted(templateId) {
    let versionMap = this.templateVersionMap || {};
    let currentId = versionMap[templateId] || templateId;
    let current = this.store.peekRecord('reports/report-template', currentId);
    return Boolean(current?.deleteTime);
  }

  // Void and edit voids the published report before it asks for the
  // replacement, so a replacement the server refuses leaves the student with
  // neither. Unlike the chips above, this one wants positive evidence: true
  // only when the newest version resolved, is in the store and isn't deleted.
  // A failed /current lookup (null in the map) or a template the map never saw
  // reads false. Public because the nested view route asks it too.
  canVoidRecreate(templateId) {
    let versionMap = this.templateVersionMap || {};
    let currentId = versionMap[templateId];
    if (!currentId) return false;
    let current = this.store.peekRecord('reports/report-template', currentId);
    return Boolean(current && !current.deleteTime);
  }

  _stateClass(state) {
    switch (state) {
      case StudentReportState.DRAFT:
        return 'draft';
      case StudentReportState.READY_TO_REVIEW:
      case StudentReportState.READY_TO_PROOFREAD:
      case StudentReportState.FEEDBACK_WAITING:
        return 'review';
      case StudentReportState.READY_TO_PUBLISH:
        return 'ready-to-publish';
      case StudentReportState.PUBLISHED:
        return 'published';
      case StudentReportState.VOID:
        return 'void';
      default:
        return '';
    }
  }

  _periodEndMonth(template, periodIndex) {
    if (!template) return 0;
    let periods = template.get('periods');
    if (!periods || !periods.get('length')) return 0;
    let sorted = periods.slice().sort((a, b) => a.month - b.month);
    let period = sorted[periodIndex] || sorted[0];
    return period ? period.get('month') : 0;
  }
}
